/**
 * Cross-platform game download utilities
 * Creates shortcuts/launchers that open the game in standalone mode
 * Internet required for gameplay (enables ads, updates, analytics)
 */

/**
 * Detect the user's platform
 */
export function detectPlatform() {
  const ua = navigator.userAgent.toLowerCase()
  const platform = navigator.platform?.toLowerCase() || ''
  
  // iOS detection
  if (/iphone|ipad|ipod/.test(ua) || (platform === 'macintel' && navigator.maxTouchPoints > 1)) {
    return 'ios'
  }
  
  // Android
  if (/android/.test(ua)) {
    return 'android'
  }
  
  // Mac
  if (/macintosh|mac os x/.test(ua)) {
    return 'mac'
  }
  
  // Windows
  if (/windows/.test(ua)) {
    return 'windows'
  }
  
  // Linux
  if (/linux/.test(ua)) {
    return 'linux'
  }
  
  return 'unknown'
}

/**
 * Detect browser type
 */
export function detectBrowser() {
  const ua = navigator.userAgent.toLowerCase()
  
  if (/edg/.test(ua)) return 'edge'
  if (/chrome/.test(ua) && !/edg/.test(ua)) return 'chrome'
  if (/safari/.test(ua) && !/chrome/.test(ua)) return 'safari'
  if (/firefox/.test(ua)) return 'firefox'
  if (/opera|opr/.test(ua)) return 'opera'
  
  return 'unknown'
}

/**
 * Check if browser supports PWA install
 */
export function supportsPWAInstall() {
  const browser = detectBrowser()
  // Chrome, Edge, Opera, Samsung Internet support beforeinstallprompt
  return ['chrome', 'edge', 'opera'].includes(browser)
}

/**
 * Get the game URL for play mode
 */
export function getGameUrl(gameId, baseUrl = window.location.origin) {
  return `${baseUrl}/play/${gameId}`
}

/**
 * Generate Windows .url shortcut file content
 */
function generateWindowsShortcut(gameTitle, gameUrl, iconUrl) {
  return `[InternetShortcut]
URL=${gameUrl}
IconIndex=0
IconFile=${iconUrl}
`
}

/**
 * Generate Mac .webloc shortcut file content (XML plist format)
 */
function generateMacShortcut(gameUrl) {
  return `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE plist PUBLIC "-//Apple//DTD PLIST 1.0//EN" "http://www.apple.com/DTDs/PropertyList-1.0.dtd">
<plist version="1.0">
<dict>
  <key>URL</key>
  <string>${gameUrl}</string>
</dict>
</plist>`
}

/**
 * Generate Linux .desktop file content
 */
function generateLinuxShortcut(gameTitle, gameUrl, iconUrl) {
  return `[Desktop Entry]
Version=1.0
Type=Link
Name=${gameTitle}
Comment=Play ${gameTitle} on Polytoria
URL=${gameUrl}
Icon=${iconUrl}
Categories=Game;
Terminal=false
`
}

/**
 * Download a file with the given content
 */
function downloadFile(filename, content, mimeType = 'text/plain') {
  const blob = new Blob([content], { type: mimeType })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/**
 * Create and download a platform-specific shortcut
 */
export function downloadShortcut(game) {
  const platform = detectPlatform()
  const gameUrl = getGameUrl(game.id)
  const iconUrl = `${window.location.origin}/icons/icon-192.png`
  const safeName = game.title.replace(/[^a-zA-Z0-9\s-]/g, '').trim()
  
  switch (platform) {
    case 'windows': {
      const content = generateWindowsShortcut(game.title, gameUrl, iconUrl)
      downloadFile(`${safeName}.url`, content)
      return { success: true, type: 'shortcut', platform: 'windows' }
    }
    
    case 'mac': {
      const content = generateMacShortcut(gameUrl)
      downloadFile(`${safeName}.webloc`, content, 'application/x-apple-web-location')
      return { success: true, type: 'shortcut', platform: 'mac' }
    }
    
    case 'linux': {
      const content = generateLinuxShortcut(game.title, gameUrl, iconUrl)
      downloadFile(`${safeName}.desktop`, content, 'application/x-desktop')
      return { success: true, type: 'shortcut', platform: 'linux' }
    }
    
    case 'ios':
    case 'android':
      // Mobile - return instructions instead of file
      return { success: false, type: 'mobile', platform }
    
    default:
      // Fallback: open in new window
      window.open(gameUrl, '_blank')
      return { success: false, type: 'fallback', platform }
  }
}

/**
 * Get platform-specific download info
 */
export function getDownloadInfo(game) {
  const platform = detectPlatform()
  const browser = detectBrowser()
  const pwaSupported = supportsPWAInstall()
  
  // Mac Safari - needs Add to Dock instructions
  if (platform === 'mac' && browser === 'safari') {
    return {
      method: 'mac-instructions',
      buttonText: 'Install Game',
      description: 'Add as standalone app to your Dock',
      icon: 'download'
    }
  }
  // PWA-capable browsers (Chrome/Edge) get install experience
  if (pwaSupported) {
    return {
      method: 'pwa',
      buttonText: 'Install Game',
      description: 'Install as a standalone app on your device',
      icon: 'download'
    }
  }
  
  // Platform-specific fallbacks
  switch (platform) {
    case 'ios':
      return {
        method: 'ios-instructions',
        buttonText: 'Add to Home Screen',
        description: 'Tap Share, then "Add to Home Screen"',
        icon: 'share'
      }
    
    case 'android':
      return {
        method: 'android-instructions',
        buttonText: 'Add to Home Screen',
        description: 'Tap menu, then "Add to Home screen"',
        icon: 'menu'
      }
    
    case 'mac':
      return {
        method: 'shortcut',
        buttonText: 'Download Game',
        description: 'Downloads a shortcut file to launch the game',
        icon: 'download'
      }
    
    case 'windows':
      return {
        method: 'shortcut',
        buttonText: 'Download Game',
        description: 'Downloads a shortcut file to launch the game',
        icon: 'download'
      }
    
    case 'linux':
      return {
        method: 'shortcut',
        buttonText: 'Download Game',
        description: 'Downloads a shortcut file to launch the game',
        icon: 'download'
      }
    
    default:
      return {
        method: 'shortcut',
        buttonText: 'Download Game',
        description: 'Downloads a shortcut file',
        icon: 'download'
      }
  }
}

/**
 * Get mobile install instructions
 */
export function getMobileInstructions(platform) {
  if (platform === 'mac') {
    return {
      title: 'Add to Dock',
      steps: [
        'In Safari, click File in the menu bar',
        'Click "Add to Dock..."',
        'Customize the name if needed and click "Add"',
        'The game will appear in your Dock as a standalone app!'
      ],
      note: 'This creates a standalone app without browser UI. Works in Safari 17+'
    }
  }

  if (platform === 'ios') {
    return {
      title: 'Add to Home Screen',
      steps: [
        'Tap the Share button at the bottom of Safari',
        'Scroll down and tap "Add to Home Screen"',
        'Tap "Add" in the top right corner',
        'The game will appear on your home screen!'
      ],
      note: 'Works best in Safari browser'
    }
  }
  
  if (platform === 'android') {
    return {
      title: 'Add to Home Screen',
      steps: [
        'Tap the menu button (⋮) in your browser',
        'Tap "Add to Home screen" or "Install app"',
        'Confirm by tapping "Add"',
        'The game will appear on your home screen!'
      ],
      note: 'Works in Chrome, Firefox, and Samsung Internet'
    }
  }
  
  return null
}
